// Parameter-change-vs-last-run diff for the APG CoPilot (CR-20260831-004).
//
// ISOLATED SIBLING of copilot-shared/opg-copilot-param-diff.ts — the SHAPE is duplicated, never
// imported across panel folders (family rule). Compares the LIVE questionnaire inputs against the
// baseline the current Run captured (Run.sentInputs) and returns a flat map of CHANGED-FIELD KEYS
// driving the yellow "this no longer matches the shown results" cues.
//
// Key grammar:
//   date.kind            — Single date | Date range toggle
//   date.value           — the picked single date
//   date.start / date.end — the range boundaries, EACH ON ITS OWN (owner vrequest 2026-09-15:
//                          "both dates are highlighted, not just the end date" — only the box
//                          that moved paints; the template still honours date.value for both)
//   exclusions.weekdays  — the Mon–Fri multi-select
//   exclusions.dates     — the specific-date exclusion set
//   bias.enabled         — the Yes | No toggle
//   bias.direction       — Up | Down | Sideways | Auto (compared only while both ends are enabled)
//
// Union semantics: when the date KIND differs only the kind key is flagged (the newly selected
// toggle paints yellow); values are compared only while the kinds match. Same for bias: an
// enabled-flip flags bias.enabled alone (the direction group appears/disappears with it).
//
// ES5 note: arrays + indexOf only (no Set/Map iteration, no for..of), matching the family.

import { RollPositionsInputs } from './apg-copilot-domain';

export type ParamChangeMap = { [key: string]: boolean };

export function hasAnyChange(map: ParamChangeMap | null): boolean {
    if (!map) {
        return false;
    }
    for (const k in map) {
        if (map.hasOwnProperty(k)) {
            return true;
        }
    }
    return false;
}

// No baseline (never run / conversation reset) => EMPTY map: nothing can be "changed since the
// run" before a run exists.
export function diffRollInputs(baseline: RollPositionsInputs | null,
                               live: RollPositionsInputs | null): ParamChangeMap {
    const out: ParamChangeMap = {};
    if (!baseline || !live) {
        return out;
    }

    const bd = baseline.date;
    const ld = live.date;
    if (bd && ld) {
        if (bd.kind !== ld.kind) {
            out['date.kind'] = true;
        } else if (bd.kind === 'single' && ld.kind === 'single') {
            if (!sameDate(bd.single, ld.single)) {
                out['date.value'] = true;
            }
        } else if (bd.kind === 'range' && ld.kind === 'range') {
            if (!sameDate(bd.start, ld.start)) {
                out['date.start'] = true;
            }
            if (!sameDate(bd.end, ld.end)) {
                out['date.end'] = true;
            }
        }
    }

    // CR-20260904-001: a weekday override is a change to the WEEKDAY exclusion the trader is
    // watching (he brought back one of the days that button excludes), so it paints the SAME cue —
    // the Day-exclusions button group. No new key, no new binding, no new CSS.
    if (!sameStringSet(baseline.weekdayExclusions, live.weekdayExclusions)
        || !sameDateSet(baseline.weekdayOverrides, live.weekdayOverrides)) {
        out['exclusions.weekdays'] = true;
    }
    if (!sameDateSet(baseline.specificDateExclusions, live.specificDateExclusions)) {
        out['exclusions.dates'] = true;
    }

    const bb = baseline.bias;
    const lb = live.bias;
    if (bb && lb) {
        if (!!bb.enabled !== !!lb.enabled) {
            out['bias.enabled'] = true;
        } else if (bb.enabled && lb.enabled && bb.direction !== lb.direction) {
            out['bias.direction'] = true;
        }
    }

    return out;
}

// Snapshot at Run fire — the reducers build fresh objects per edit, but nothing ENFORCES that the
// live reference is never mutated later, so the baseline gets its own copy (the "snapshot of what
// was sent" rule, same as the OPG copilot's cloneTabInputs).
export function cloneRollInputs(i: RollPositionsInputs): RollPositionsInputs {
    return {
        date: i.date.kind === 'single'
            ? { kind: 'single', single: cloneDate(i.date.single) }
            : { kind: 'range', start: cloneDate(i.date.start), end: cloneDate(i.date.end) },
        weekdayExclusions: i.weekdayExclusions.slice(),
        specificDateExclusions: i.specificDateExclusions.map(d => cloneDate(d) as Date),
        // Own copy, like every other set: without it the snapshot would ALIAS the live array and a
        // later override would retro-change what the run is recorded as having sent.
        weekdayOverrides: (i.weekdayOverrides || []).map(d => cloneDate(d) as Date),
        bias: { enabled: i.bias.enabled, direction: i.bias.direction }
    };
}

// ---- primitives ---------------------------------------------------------------------------

function cloneDate(d: Date | null): Date | null {
    return d instanceof Date ? new Date(d.getTime()) : null;
}

function sameDate(x: Date | null, y: Date | null): boolean {
    const xv = x instanceof Date && !isNaN(x.getTime());
    const yv = y instanceof Date && !isNaN(y.getTime());
    if (!xv || !yv) {
        return xv === yv;
    }
    return (x as Date).getTime() === (y as Date).getTime();
}

// Order-insensitive: toggling Mon off and back on is not a change.
function sameStringSet(a: string[], b: string[]): boolean {
    if (!a || !b) {
        return (!a || a.length === 0) === (!b || b.length === 0);
    }
    if (a.length !== b.length) {
        return false;
    }
    for (let i = 0; i < a.length; i++) {
        if (b.indexOf(a[i]) < 0) {
            return false;
        }
    }
    return true;
}

function sameDateSet(a: Date[], b: Date[]): boolean {
    const at = validTimes(a);
    const bt = validTimes(b);
    if (at.length !== bt.length) {
        return false;
    }
    for (let i = 0; i < at.length; i++) {
        if (bt.indexOf(at[i]) < 0) {
            return false;
        }
    }
    return true;
}

function validTimes(list: Date[]): number[] {
    const out: number[] = [];
    if (!list) {
        return out;
    }
    for (let i = 0; i < list.length; i++) {
        const d = list[i];
        if (d instanceof Date && !isNaN(d.getTime()) && out.indexOf(d.getTime()) < 0) {
            out.push(d.getTime());
        }
    }
    return out;
}
